/**
 * Outbox — persistent queue of pending server actions.
 *
 * Key contract:
 *   - `enqueue()` returns the OutboxItem.id, which doubles as the
 *     server-side idempotencyKey. The server-side concurrency primitives
 *     (safe-stock, etc.) use this key to deduplicate retries.
 *   - `enqueue()` is synchronous from the UI's perspective: a sale is
 *     "recorded" the moment it lands in the outbox, even if offline.
 *   - The sync engine reads the queue, attempts each item, and transitions
 *     status. The UI subscribes to changes via useOutbox().
 *
 * Eventing:
 *   Browsers don't ship an IndexedDB change feed. We dispatch a custom
 *   "eaze:outbox-changed" event on window after every mutation so React
 *   hooks can refresh. This is cheap and works across tabs via the
 *   BroadcastChannel API (covered in sync-engine).
 */
import {
  getDB,
  scopeKey,
  generateId,
  type OutboxItem,
  type OutboxStatus,
  type OutboxActionType,
} from "./db";

const OUTBOX_EVENT = "eaze:outbox-changed";

/** Dispatch a same-tab change notification. */
function notifyChanged(detail?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OUTBOX_EVENT, { detail }));
}

/** Subscribe to outbox changes. Returns an unsubscribe function. */
export function onOutboxChanged(
  handler: (detail: Record<string, unknown> | undefined) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = (e: Event) => {
    const ce = e as CustomEvent;
    handler(ce.detail);
  };
  window.addEventListener(OUTBOX_EVENT, listener);
  return () => window.removeEventListener(OUTBOX_EVENT, listener);
}

// ── Enqueue ──────────────────────────────────────────────────────

export interface EnqueueInput {
  type: OutboxActionType;
  endpoint: string;
  method?: "POST" | "PATCH" | "DELETE" | "PUT";
  payload: Record<string, unknown>;
  companyId: string;
  businessId: string;
  userId?: string | null;
  summary: string;
  /** Provide a deterministic id when the action is naturally idempotent
   *  (e.g. order with a client-generated orderId). Otherwise a UUID is
   *  generated. */
  id?: string;
}

/**
 * Append an action to the outbox. Returns the saved row.
 *
 * The action becomes immediately visible to UIs via the "by-scope" index.
 * The sync engine (separate module) drains it.
 */
export async function enqueue(input: EnqueueInput): Promise<OutboxItem> {
  const now = Date.now();
  const id = input.id ?? generateId();
  const item: OutboxItem & { scopeKey: string } = {
    id,
    type: input.type,
    endpoint: input.endpoint,
    method: input.method ?? "POST",
    payload: {
      // Inject idempotencyKey into the payload so the server doesn't need
      // a separate header to find it. The server can still look at the
      // X-Idempotency-Key header if it prefers — the sync engine sets both.
      idempotencyKey: id,
      ...input.payload,
    },
    companyId: input.companyId,
    businessId: input.businessId,
    userId: input.userId ?? null,
    summary: input.summary,
    status: "pending",
    attempts: 0,
    lastError: null,
    lastErrorDetail: null,
    createdAt: now,
    updatedAt: now,
    nextRetryAt: null,
    serverResponse: null,
    // Synthesized index key — see db.ts
    scopeKey: scopeKey(input.companyId, input.businessId),
  };

  const db = await getDB();
  await db.put("outbox", item);
  notifyChanged({ id, action: "enqueue" });

  return item;
}

// ── List ─────────────────────────────────────────────────────────

/** All outbox items scoped to the given (companyId, businessId). */
export async function listByScope(
  companyId: string,
  businessId: string,
  filter?: { status?: OutboxStatus | OutboxStatus[] },
): Promise<OutboxItem[]> {
  const db = await getDB();
  // We could query by index 'by-scope' for performance but the typical
  // queue stays small (< few hundred entries). A full scan + filter is
  // simpler and the JIT handles it.
  const all = await db.getAll("outbox");
  const wantedStatuses = filter?.status
    ? new Set(Array.isArray(filter.status) ? filter.status : [filter.status])
    : null;
  return all
    .filter(
      (i: OutboxItem) =>
        i.companyId === companyId && i.businessId === businessId,
    )
    .filter((i: OutboxItem) =>
      wantedStatuses ? wantedStatuses.has(i.status) : true,
    )
    .sort((a: OutboxItem, b: OutboxItem) => a.createdAt - b.createdAt);
}

/** Get a single item by id. */
export async function getById(id: string): Promise<OutboxItem | undefined> {
  const db = await getDB();
  return db.get("outbox", id);
}

// ── Status transitions ───────────────────────────────────────────

/**
 * Pull the next batch of items eligible for sending. Sorted by createdAt
 * ascending. Skips items still in cool-down (nextRetryAt > now) and items
 * in non-retryable states (conflict, done).
 */
export async function getNextDrainable(
  companyId: string,
  businessId: string,
  limit = 10,
  userId?: string | null,
): Promise<OutboxItem[]> {
  const items = await listByScope(companyId, businessId, {
    status: ["pending", "failed"],
  });
  const now = Date.now();
  return items
    // A sale is sent under the cashier who made it: another person signed
    // in on this till leaves it waiting for them (it would be credited to
    // the wrong cashier and shift otherwise).
    .filter((i) => !userId || !i.userId || i.userId === userId)
    .filter((i) => i.nextRetryAt == null || i.nextRetryAt <= now)
    .slice(0, limit);
}

/** Mark in-flight before sending. Increments `attempts`. */
export async function markInFlight(id: string): Promise<void> {
  const db = await getDB();
  const item = await db.get("outbox", id);
  if (!item) return;
  await db.put("outbox", {
    ...item,
    status: "in_flight",
    attempts: item.attempts + 1,
    updatedAt: Date.now(),
  });
  notifyChanged({ id, action: "in_flight" });
}

/** Successful sync — store the response for confirmation UI. */
export async function markDone(
  id: string,
  serverResponse: Record<string, unknown> | null,
): Promise<void> {
  const db = await getDB();
  const item = await db.get("outbox", id);
  if (!item) return;
  await db.put("outbox", {
    ...item,
    status: "done",
    lastError: null,
    lastErrorDetail: null,
    serverResponse,
    updatedAt: Date.now(),
  });
  notifyChanged({ id, action: "done" });
}

/** Transient failure (network, 5xx). Will retry per backoff. */
export async function markFailed(
  id: string,
  error: { message: string; detail?: Record<string, unknown> },
  backoffMs: number,
): Promise<void> {
  const db = await getDB();
  const item = await db.get("outbox", id);
  if (!item) return;
  await db.put("outbox", {
    ...item,
    status: "failed",
    lastError: error.message,
    lastErrorDetail: error.detail ?? null,
    nextRetryAt: Date.now() + backoffMs,
    updatedAt: Date.now(),
  });
  notifyChanged({ id, action: "failed" });
}

/** Permanent failure — server returned 4xx that operator must resolve. */
export async function markConflict(
  id: string,
  error: { message: string; detail?: Record<string, unknown> },
): Promise<void> {
  const db = await getDB();
  const item = await db.get("outbox", id);
  if (!item) return;
  await db.put("outbox", {
    ...item,
    status: "conflict",
    lastError: error.message,
    lastErrorDetail: error.detail ?? null,
    nextRetryAt: null,
    updatedAt: Date.now(),
  });
  notifyChanged({ id, action: "conflict" });
}

/** Reset a conflict back to pending after operator manually adjusts. */
export async function resetConflict(
  id: string,
  patchedPayload?: Record<string, unknown>,
): Promise<void> {
  const db = await getDB();
  const item = await db.get("outbox", id);
  if (!item) return;
  await db.put("outbox", {
    ...item,
    status: "pending",
    lastError: null,
    lastErrorDetail: null,
    nextRetryAt: null,
    updatedAt: Date.now(),
    payload: patchedPayload
      ? { ...item.payload, ...patchedPayload, idempotencyKey: item.id }
      : item.payload,
  });
  notifyChanged({ id, action: "reset" });
}

/** Operator gives up on a conflict and removes it from the queue. */
export async function discardItem(id: string): Promise<void> {
  const db = await getDB();
  await db.delete("outbox", id);
  notifyChanged({ id, action: "discard" });
}

/**
 * Items left "in_flight" by a tab that closed (or crashed) mid-send would
 * never be picked up again. Put them back in the queue; the server's
 * idempotency key makes a resend safe even if the first one landed.
 */
export async function recoverStuck(olderThanMs = 60_000): Promise<number> {
  const db = await getDB();
  const all = await db.getAll("outbox");
  const cutoff = Date.now() - olderThanMs;
  const stuck = all.filter((i: OutboxItem) => i.status === "in_flight" && i.updatedAt < cutoff);
  for (const i of stuck) {
    await db.put("outbox", { ...i, status: "failed", nextRetryAt: null, updatedAt: Date.now() });
  }
  if (stuck.length) notifyChanged({ action: "recover", count: stuck.length });
  return stuck.length;
}

// ── Cleanup ──────────────────────────────────────────────────────

/**
 * Delete successfully-synced items older than `olderThanMs` (default 24h).
 * Call this periodically to keep the queue lean. Conflict and pending
 * items are NEVER auto-deleted.
 */
export async function gc(
  olderThanMs = 24 * 60 * 60 * 1000,
): Promise<{ removed: number }> {
  const db = await getDB();
  const all = await db.getAll("outbox");
  const cutoff = Date.now() - olderThanMs;
  const toDelete = all.filter(
    (i: OutboxItem) => i.status === "done" && i.updatedAt < cutoff,
  );
  const tx = db.transaction("outbox", "readwrite");
  await Promise.all([
    ...toDelete.map((i: OutboxItem) => tx.store.delete(i.id)),
    tx.done,
  ]);
  if (toDelete.length > 0)
    notifyChanged({ action: "gc", removed: toDelete.length });
  return { removed: toDelete.length };
}

// ── Stats (for UI badges) ────────────────────────────────────────

export interface OutboxStats {
  pending: number;
  in_flight: number;
  failed: number;
  conflict: number;
  done: number;
  total: number;
}

export async function getStats(
  companyId: string,
  businessId: string,
): Promise<OutboxStats> {
  const items = await listByScope(companyId, businessId);
  const stats: OutboxStats = {
    pending: 0,
    in_flight: 0,
    failed: 0,
    conflict: 0,
    done: 0,
    total: items.length,
  };
  for (const i of items) {
    stats[i.status] += 1;
  }
  return stats;
}

// ── Build-fix: re-export types so consumers can import from outbox
export type { OutboxItem, OutboxStatus, OutboxActionType } from "./db";
